import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DocumentGasto {
  id: string;
  embarque_id: string | null;
  concepto: string;
  monto: number;
  monto_base: number | null;
  impuestos_locales: number | null;
  moneda: string;
  trm_pago: number | null;
  base_prorrateo: string;
  aplica_costeo_inventario: boolean;
  confianza_ocr: number | null;
  /** B-02A — true ⇒ monto_base se derivó de monto/1.19 por fallo OCR. */
  alerta_ocr_base: boolean;
  notas: string | null;
  created_at: string;
}

/**
 * Lectura aislada de los gastos extraídos por OCR para un documento específico.
 * Usado por AuditPanel y EditDocumentDrawer en la pestaña "Gastos Extraídos"
 * para documentos de tipo gasto (BL, Guía Aérea, Doc. Puerto, Póliza, Factura de Compras).
 *
 * Incluye desglose financiero (monto_base + impuestos_locales = monto) para
 * transparencia tipo CFO/agente de aduanas.
 */
export function useDocumentGastos(documentoId: string | null | undefined) {
  const { user, orgId } = useAuth();

  return useQuery({
    queryKey: ["document-gastos", orgId, documentoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gastos_embarque")
        .select(
          "id, embarque_id, concepto, monto, monto_base, impuestos_locales, moneda, trm_pago, base_prorrateo, aplica_costeo_inventario, confianza_ocr, alerta_ocr_base, notas, created_at",
        )
        .eq("documento_id", documentoId!)
        .eq("org_id", orgId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DocumentGasto[];
    },
    enabled: !!user && !!orgId && !!documentoId,
  });
}
